import React, { useContext, useState, useEffect, useRef, useMemo } from "react";
import { DatabaseContext } from "../../redux/DatabaseStore";
import { getRequest } from "../../api/ApiController";
import '../../assets/css/style.css';
import '../../assets/css/iconsmenu.css';
import AuthContext from "../../components/AuthContext";
import UtilContext from "../../components/UtilContext";
import { LocaleContext } from "../../components/LocaleContext";
import {
    buildPreviewPeriodOptionLabels,
    registerPreviewCalendarLocale,
} from "../../utils/previewUiLocale";
import { Chart } from 'primereact/chart';
import { Calendar } from '../../components/PrimeCalendar';
import { Skeleton } from 'primereact/skeleton';
import { addLocale } from 'primereact/api';
import { RefreshIcon } from "../svg/svgIcons";

const PERIOD_OPTIONS = ['Všetko', 'Za deň', 'Za mesiac', 'Za rok', 'Vlastné'];
const PREVIEW_PAGE = 'PaintingPreview';

/** Dátum ako YYYY-MM-DD v lokálnom čase (nie UTC), aby Za mesiac 02/2024 neprešiel na január. */
function toLocalDateString(d) {
  if (!d || !(d instanceof Date) || isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseSavedPeriod(attrs) {
  if (!attrs?.preview_period || typeof attrs.preview_period.timePeriod !== 'string') return null;
  if (!PERIOD_OPTIONS.includes(attrs.preview_period.timePeriod)) return null;
  const { timePeriod } = attrs.preview_period;
  const date = attrs.preview_period.date;
  if (timePeriod === 'Všetko') return { timePeriod, date: null };
  if (timePeriod === 'Vlastné' && Array.isArray(date) && date.length >= 2) {
    return { timePeriod, date: date.map((d) => (d != null ? new Date(d) : null)) };
  }
  if (date != null) {
    const d = new Date(date);
    if (!isNaN(d.getTime())) return { timePeriod, date: d };
  }
  return null;
}

function serializePeriod(timePeriod, date) {
  const payload = { timePeriod };
  if (date == null) payload.date = null;
  else if (Array.isArray(date)) payload.date = date.map((d) => (d instanceof Date ? toLocalDateString(d) : d));
  else payload.date = date instanceof Date ? toLocalDateString(date) : date;
  return payload;
}

export default function PreviewLak() {

  const [state,dispatch] = useContext(DatabaseContext);
  const {user} = useContext(AuthContext);
  const { setUserMaskFilter } = useContext(UtilContext);
  const { t, locale } = useContext(LocaleContext);
    const periodOptionLabels = useMemo(() => buildPreviewPeriodOptionLabels(t), [t]);
    const [calendarLocale, setCalendarLocale] = useState(() => registerPreviewCalendarLocale(addLocale, locale, t));
    useEffect(() => { setCalendarLocale(registerPreviewCalendarLocale(addLocale, locale, t)); }, [locale, t]);

  const [timePeriod, setTimePeriod] = useState('Všetko');
  const [date, setDate] = useState(null);
  const [dateProps, setDateProps] = useState({ hidden: true });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const handleRefresh = () => setRefreshTrigger((n) => n + 1);
  const appliedSavedRef = useRef(false);

  useEffect(() => {
    if (appliedSavedRef.current) return;
    if (!state.user_mask_filters?.length) return;
    appliedSavedRef.current = true;
    const mask = (state.user_mask_filters || []).find((m) => m.page === PREVIEW_PAGE);
    const attrs = mask?.attributes;
    const parsed = typeof attrs === 'string' ? (() => { try { return parseSavedPeriod(JSON.parse(attrs)); } catch { return null; } })() : parseSavedPeriod(attrs);
    if (parsed) {
      setTimePeriod(parsed.timePeriod);
      setDate(parsed.date ?? (parsed.timePeriod === 'Všetko' ? null : new Date()));
      if (parsed.timePeriod === 'Za deň') setDateProps({ dateFormat: 'dd.mm.yy' });
      else if (parsed.timePeriod === 'Za mesiac') setDateProps({ view: 'month', dateFormat: 'mm.yy' });
      else if (parsed.timePeriod === 'Za rok') setDateProps({ view: 'year', dateFormat: 'yy' });
      else if (parsed.timePeriod === 'Vlastné') setDateProps({ dateFormat: 'dd.mm.yy', selectionMode: 'range' });
      else setDateProps({ hidden: true });
    }
  }, [state.user_mask_filters]);

  const savePeriodToDb = () => {
    setUserMaskFilter(PREVIEW_PAGE, 'set_attributes', null, null, { preview_period: serializePeriod(timePeriod, date) });
  };

  const [statsFromApi, setStatsFromApi] = useState({ painting: [0, 0], numberOfPainting: [0, 0, 0] });
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    setStatsLoading(true);
    const params = new URLSearchParams();
    params.set('time_period', timePeriod);
    if (timePeriod === 'Vlastné' && Array.isArray(date) && date.length >= 2) {
      const d0 = date[0] instanceof Date ? date[0] : new Date(date[0]);
      const d1 = date[1] instanceof Date ? date[1] : new Date(date[1]);
      params.set('date_from', toLocalDateString(d0));
      params.set('date_to', toLocalDateString(d1));
    } else if (date) {
      const d = date instanceof Date ? date : new Date(date);
      params.set('date', toLocalDateString(d));
    }
    getRequest('/api/painting_conditions/preview_stats?' + params.toString())
      .then((res) => {
        const data = res?.data ?? res;
        setStatsFromApi({
          painting: Array.isArray(data.painting) ? data.painting : [0, 0],
          numberOfPainting: Array.isArray(data.numberOfPainting) ? data.numberOfPainting : [0, 0, 0],
        });
      })
      .catch(() => setStatsFromApi({ painting: [0, 0], numberOfPainting: [0, 0, 0] }))
      .finally(() => setStatsLoading(false));
  }, [timePeriod, date, refreshTrigger]);

  const paintingRecordsData = useMemo(() => ({
    labels: ['OK', 'NOK'],
    datasets: [{
      data: statsFromApi.painting,
      backgroundColor: ['#15803d', '#b91c1c'],
      hoverBackgroundColor: ['#15803d', '#b91c1c'],
    }],
  }), [statsFromApi.painting]);

  const numberOfPaintingRecordsData = useMemo(() => ({
    labels: [t('PaintingPreview.NoRecord'), t('PaintingPreview.OneRecord'), t('PaintingPreview.MoreRecords')],
    datasets: [{
      data: statsFromApi.numberOfPainting,
      backgroundColor: ['#6b7280', '#ca8a04', '#15803d'],
      hoverBackgroundColor: ['#6b7280', '#ca8a04', '#15803d'],
    }],
  }), [statsFromApi.numberOfPainting, t]);

  const [legendHidden, setLegendHidden] = useState({ painting: [], numberOfPainting: [] });
  const originalDataRef = useRef({ painting: [], numberOfPainting: [] });
  const originalTotal = (key, fallbackArr) => {
    // primárne používaj aktuálne dáta z grafu (fallbackArr),
    // aby sa celkový počet hneď prepočítal pri zmene filtra
    const base = (fallbackArr && fallbackArr.length) ? fallbackArr : originalDataRef.current[key];
    const src = base ?? [];
    return src.reduce((a, b) => a + (Number(b) || 0), 0);
  };
  const paintingChartRef = useRef(null);
  const numberOfPaintingChartRef = useRef(null);

  const toggleLegendItem = (chartKey, index, chartRef) => () => {
    const ch = chartRef?.current?.getChart?.();
    if (!ch) return;
    const originals = originalDataRef.current[chartKey];
    if (!Array.isArray(originals)) return;
    const currentlyHidden = legendHidden[chartKey]?.[index];
    const nextHidden = !currentlyHidden;
    const hiddenArr = legendHidden[chartKey] ?? [];
    const newData = originals.map((val, i) => {
      const isHidden = i === index ? nextHidden : hiddenArr[i];
      return isHidden ? 0 : (Number(val) || 0);
    });
    const ds = ch.data.datasets[0];
    ds.data = [...newData];
    const meta = ch.getDatasetMeta?.(0);
    if (meta && Array.isArray(meta._parsed)) newData.forEach((v, i) => { meta._parsed[i] = v; });
    ch.update();
    setLegendHidden((prev) => {
      const arr = prev[chartKey] ?? [];
      const next = [...arr];
      next[index] = nextHidden;
      return { ...prev, [chartKey]: next };
    });
  };

  useEffect(() => {
    const d = (src) => (src?.datasets?.[0]?.data ? [...src.datasets[0].data] : []);
    originalDataRef.current = {
      painting: d(paintingRecordsData),
      numberOfPainting: d(numberOfPaintingRecordsData),
    };
  }, [paintingRecordsData, numberOfPaintingRecordsData]);

  

    const paintingNotFullFilledOptions = {
        plugins: { legend: { display: false } },
        onClick(evt, item) {
            if (!item?.length) return;
            if (item[0].index === 1) {
                dispatch({ type: 'create_new_tab', name: t('PaintingPreview.ConditionsTitle'), translationKey: 'PaintingPreview.ConditionsTitle', legacyLabel: t('PaintingPreview.ConditionsTitle'), page: 'PaintingConditionList', props: { preview: 'count', timePeriod, date, countType: 'partial' }, mask: 'pain002' });
            } else if (item[0].index === 2) {
                dispatch({ type: 'create_new_tab', name: t('PaintingPreview.ConditionsTitle'), translationKey: 'PaintingPreview.ConditionsTitle', legacyLabel: t('PaintingPreview.ConditionsTitle'), page: 'PaintingConditionList', props: { preview: 'count', timePeriod, date, countType: 'full' }, mask: 'pain002' });
            }
        },
    };

    const paintingFullFilledOptions = {
        plugins: { legend: { display: false } },
        onClick(evt, item) {
            if (!item?.length) return;
            if (item[0].index === 0) {
                dispatch({ type: 'create_new_tab', name: t('PaintingPreview.ConditionsTitle'), translationKey: 'PaintingPreview.ConditionsTitle', legacyLabel: t('PaintingPreview.ConditionsTitle'), page: 'PaintingConditionList', props: { preview: 'OK', timePeriod, date }, mask: 'pain002' });
            } else if (item[0].index === 1) {
                dispatch({ type: 'create_new_tab', name: t('PaintingPreview.ConditionsTitle'), translationKey: 'PaintingPreview.ConditionsTitle', legacyLabel: t('PaintingPreview.ConditionsTitle'), page: 'PaintingConditionList', props: { preview: 'NOK', timePeriod, date }, mask: 'pain002' });
            }
        },
    };

    const plugin = {
        id: 'emptyDoughnut',
        afterDraw(chart) {
            const data = chart.data?.datasets?.[0]?.data ?? [];
            const count = data.reduce((a, b) => a + (b ?? 0), 0);
            if (count < 1) {
                const ctx = chart.ctx;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.font = '20px Arial';
                ctx.fillStyle = '#9ca3af';
                ctx.fillText(t('ServiceTaskPreview.NoRecords'), chart.width / 2, chart.height / 2);
            }
        },
    };

      function changeTimePeriod(time_period){
        setTimePeriod(time_period);
        setDate(null);
        if(time_period=='Za deň'){
            setDateProps({dateFormat:"dd.mm.yy"});
        }
        else if(time_period=="Za mesiac"){
            setDateProps({view:"month",dateFormat:"mm.yy"});
        }
        else if(time_period=="Za rok"){
            setDateProps({view:"year",dateFormat:"yy"});
        }
        else if(time_period=='Vlastné'){
            setDateProps({dateFormat:"dd.mm.yy",selectionMode:"range"});
        }
        else{
            setDateProps({hidden:true});
        }
    }

    function showResult(new_date){
        setDate(new_date);
    }


    return (
        <div className="min-h-[calc(100vh-137px)]">
      <div className="lg:grid grid-cols-3 gap-1">
            <div />
            <div>
                <div className="flex items-center gap-3 mt-4">
                    <label htmlFor="time_period" className="text-xl font-semibold text-gray-800 shrink-0">{t('Common.Period')}:</label>
                    <select
                        name="time_period"
                        value={timePeriod}
                        onChange={(e) => changeTimePeriod(e.target.value)}
                        className="bg-white flex-1 min-w-0 px-4 py-2 text-gray-700 border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                    >
                    <option value="Všetko">{periodOptionLabels['Všetko']}</option>
                    <option value="Za deň">{periodOptionLabels['Za deň']}</option>
                    <option value="Za mesiac">{periodOptionLabels['Za mesiac']}</option>
                    <option value="Za rok">{periodOptionLabels['Za rok']}</option>
                    <option value="Vlastné">{periodOptionLabels['Vlastné']}</option>
                </select>
                    <button
                        type="button"
                        onClick={handleRefresh}
                        title={t('Common.RefreshData')}
                        className="flex items-center justify-center w-10 h-10 shrink-0 rounded-md border border-gray-300 bg-gray-600 hover:bg-gray-700 text-white transition-colors"
                    >
                        <RefreshIcon />
                    </button>
                    <button
                        type="button"
                        onClick={savePeriodToDb}
                        title={t('ServiceTaskPreview.SavePeriodHint')}
                        className="shrink-0 px-3 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring focus:ring-gray-200"
                    >
                        {t('Common.SaveMask')}
                    </button>
                </div>
            </div>
            <div />
            <div />
            <div className="flex-auto">
                <Calendar
                    value={date}
                    onChange={(e) => showResult(e.target.value)}
                    showIcon
                    className="w-full"
                    locale={calendarLocale}
                    {...dateProps}
                    hideOnRangeSelection
                    style={{ border: '1px solid #e5e7eb', borderRadius: '6px' }}
                />
            </div>
            <div />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-4">
          <div className="bg-gradient-to-r from-gray-50 via-gray-100 to-gray-200 rounded-xl shadow-md p-4">
            <h2 className="text-center text-xl font-semibold mb-4">{t('PaintingPreview.ConditionsTitle')}</h2>
            <div className="w-full max-w-[420px] h-[420px] mx-auto flex items-center justify-center">
              {statsLoading ? <Skeleton shape="circle" size="320px" className="flex-shrink-0" /> : (
              <Chart
                ref={paintingChartRef}
                type="doughnut"
                data={paintingRecordsData}
                options={paintingFullFilledOptions}
                className="w-full h-full"
                plugins={[plugin]}
              />
              )}
            </div>
            {!statsLoading && (
            <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-2 text-sm text-gray-700">
              {paintingRecordsData.labels?.map((label, i) => (
                <button key={i} type="button" onClick={toggleLegendItem('painting', i, paintingChartRef)} className="inline-flex items-center gap-1.5 cursor-pointer bg-transparent border-0 p-0 text-left hover:opacity-80" style={{ textDecoration: legendHidden.painting?.[i] ? 'line-through' : undefined, opacity: legendHidden.painting?.[i] ? 0.5 : 1 }}>
                  <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: paintingRecordsData.datasets?.[0]?.backgroundColor?.[i] ?? '#6b7280' }} />
                  {label}
                </button>
              ))}
            </div>
            )}
            <div className="text-center font-semibold mt-4">
              {statsLoading ? <Skeleton width="8rem" height="1.5rem" className="mx-auto" /> : t('PaintingPreview.TotalCount', { count: originalTotal('painting', paintingRecordsData?.datasets?.[0]?.data) })}
            </div>
          </div>
          <div className="bg-gradient-to-r from-gray-50 via-gray-100 to-gray-200 rounded-xl shadow-md p-4">
            <h2 className="text-center text-xl font-semibold mb-4">{t('PaintingPreview.RecordCountTitle')}</h2>
            <div className="w-full max-w-[420px] h-[420px] mx-auto flex items-center justify-center">
            {statsLoading ? <Skeleton shape="circle" size="320px" className="flex-shrink-0" /> : (
            <Chart
              ref={numberOfPaintingChartRef}
              type="doughnut"
              data={numberOfPaintingRecordsData}
              options={paintingNotFullFilledOptions}
              className="w-full h-full"
              plugins={[plugin]}
            />
            )}
            </div>
            {!statsLoading && (
            <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-2 text-sm text-gray-700">
              {numberOfPaintingRecordsData.labels?.map((label, i) => (
                <button key={i} type="button" onClick={toggleLegendItem('numberOfPainting', i, numberOfPaintingChartRef)} className="inline-flex items-center gap-1.5 cursor-pointer bg-transparent border-0 p-0 text-left hover:opacity-80" style={{ textDecoration: legendHidden.numberOfPainting?.[i] ? 'line-through' : undefined, opacity: legendHidden.numberOfPainting?.[i] ? 0.5 : 1 }}>
                  <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: numberOfPaintingRecordsData.datasets?.[0]?.backgroundColor?.[i] ?? '#6b7280' }} />
                  {label}
                </button>
              ))}
            </div>
            )}
            <div className="text-center font-semibold mt-4">
              {statsLoading ? <Skeleton width="8rem" height="1.5rem" className="mx-auto" /> : t('PaintingPreview.TotalCount', { count: originalTotal('numberOfPainting', numberOfPaintingRecordsData?.datasets?.[0]?.data) })}
            </div>
          </div>
          <div className="bg-gradient-to-r from-gray-100 to-gray-200 rounded-xl shadow-md p-4 min-h-[420px] flex items-center justify-center bg-gray-200/60">
            {/* Prázdne sivé okno */}
          </div>
        </div>
</div>
    );
}
